/**
 * Grafici dentro i report.
 *
 * Un report è Markdown, e un grafico ci entra come blocco recintato con la
 * lingua `chart` e dentro un JSON: l'agente lo scrive come scriverebbe una
 * tabella, e un lettore che non sa cos'è vede comunque un blocco di codice.
 * I dati sono nel blocco e nient'altro: niente URL, niente script, niente
 * chiamate. Il disegno lo fa il frontend, e questo file decide cosa è un
 * grafico valido, sia per chi scrive (che riceve un errore leggibile da un
 * agente) sia per chi disegna (che ricontrolla, e in caso di dubbio mostra il
 * blocco di codice).
 */
export const CHART_TYPES = ['bar', 'line', 'donut'];
export const CHART_LIMITS = {
    /** Caratteri del blocco: un grafico è una manciata di numeri, non un dataset. */
    maxSourceLength: 8192,
    maxTitleLength: 120,
    maxUnitLength: 12,
    maxLabels: 24,
    maxSeries: 6,
    /** Sei colori distinguibili: oltre, le fette diventano indistinguibili. */
    maxDonutSlices: 6,
    maxNameLength: 40,
    maxAbsValue: 1e12,
    maxChartsPerReport: 12,
};
const KNOWN_KEYS = new Set(['type', 'title', 'unit', 'labels', 'series']);
function fail(code, message) {
    return { ok: false, error: { code, message } };
}
function isText(value, max) {
    return typeof value === 'string' && value.trim().length > 0 && value.length <= max;
}
/** Legge il contenuto di un blocco `chart` e lo trasforma in una specifica valida. */
export function parseChartSpec(source) {
    const { maxSourceLength, maxTitleLength, maxUnitLength, maxLabels, maxSeries, maxNameLength, maxAbsValue, } = CHART_LIMITS;
    if (source.length > maxSourceLength) {
        return fail('too_long', `A chart block can hold at most ${maxSourceLength} characters`);
    }
    let raw;
    try {
        raw = JSON.parse(source);
    }
    catch {
        return fail('invalid_json', 'The chart block must be valid JSON');
    }
    if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
        return fail('not_an_object', 'The chart block must be a JSON object');
    }
    const input = raw;
    for (const key of Object.keys(input)) {
        if (!KNOWN_KEYS.has(key)) {
            return fail('unknown_key', `Unknown key "${key.slice(0, 40)}": use type, title, unit, labels and series`);
        }
    }
    const type = input.type;
    if (typeof type !== 'string' || !CHART_TYPES.includes(type)) {
        return fail('invalid_type', `"type" must be one of: ${CHART_TYPES.join(', ')}`);
    }
    if (input.title !== undefined && !isText(input.title, maxTitleLength)) {
        return fail('invalid_title', `"title" must be a text of at most ${maxTitleLength} characters`);
    }
    if (input.unit !== undefined && !isText(input.unit, maxUnitLength)) {
        return fail('invalid_unit', `"unit" must be a text of at most ${maxUnitLength} characters`);
    }
    const labels = input.labels;
    if (!Array.isArray(labels) ||
        labels.length < 1 ||
        labels.length > maxLabels ||
        !labels.every((label) => isText(label, maxNameLength))) {
        return fail('invalid_labels', `"labels" must be 1 to ${maxLabels} non-empty texts of at most ${maxNameLength} characters`);
    }
    const rawSeries = input.series;
    if (!Array.isArray(rawSeries) || rawSeries.length < 1 || rawSeries.length > maxSeries) {
        return fail('invalid_series', `"series" must be a list of 1 to ${maxSeries} series`);
    }
    const series = [];
    const names = new Set();
    for (const item of rawSeries) {
        if (item === null || typeof item !== 'object' || Array.isArray(item)) {
            return fail('invalid_series', 'Each series must be an object with "name" and "values"');
        }
        const { name, values, ...rest } = item;
        if (Object.keys(rest).length > 0) {
            return fail('invalid_series', 'A series has only "name" and "values"');
        }
        if (!isText(name, maxNameLength)) {
            return fail('invalid_series', `Each series needs a "name" of at most ${maxNameLength} characters`);
        }
        if (names.has(name)) {
            return fail('duplicate_series_name', `The series name "${name}" is used twice`);
        }
        names.add(name);
        if (!Array.isArray(values) || values.length !== labels.length) {
            return fail('length_mismatch', `Series "${name}" needs exactly ${labels.length} values, one per label`);
        }
        if (!values.every((v) => typeof v === 'number' && Number.isFinite(v) && Math.abs(v) <= maxAbsValue)) {
            return fail('invalid_value', `Series "${name}" must hold plain numbers no larger than ${maxAbsValue} in absolute value`);
        }
        series.push({ name, values: values });
    }
    if (type === 'donut') {
        if (series.length !== 1) {
            return fail('donut_one_series', 'A donut chart has exactly one series');
        }
        const values = series[0].values;
        if (values.length > CHART_LIMITS.maxDonutSlices) {
            return fail('too_many_slices', `A donut chart has at most ${CHART_LIMITS.maxDonutSlices} slices: group the smallest under "Other"`);
        }
        if (values.some((v) => v < 0)) {
            return fail('negative_donut', 'A donut chart cannot hold negative values');
        }
        if (values.reduce((sum, v) => sum + v, 0) <= 0) {
            return fail('empty_donut', 'A donut chart needs values that add up to more than zero');
        }
    }
    const spec = { type: type, labels: labels, series };
    if (input.title !== undefined)
        spec.title = input.title.trim();
    if (input.unit !== undefined)
        spec.unit = input.unit.trim();
    return { ok: true, spec };
}
/**
 * I contenuti dei blocchi `chart` di un testo Markdown, nell'ordine in cui
 * compaiono.
 *
 * Segue le regole dei recinti di CommonMark che contano qui: apre con almeno
 * tre apici o tilde, chiude con lo stesso carattere in numero uguale o
 * maggiore, e dentro un recinto aperto niente è un altro recinto. Così un
 * report che *spiega* la sintassi dentro un blocco `md` non passa per un
 * grafico.
 */
export function extractChartBlocks(markdown) {
    const blocks = [];
    const lines = markdown.split(/\r?\n/);
    let fence = null;
    let body = [];
    for (const line of lines) {
        if (fence) {
            const close = /^ {0,3}(`{3,}|~{3,})\s*$/.exec(line);
            if (close && close[1][0] === fence.char && close[1].length >= fence.length) {
                if (fence.isChart)
                    blocks.push(body.join('\n'));
                fence = null;
                body = [];
            }
            else if (fence.isChart) {
                body.push(line);
            }
            continue;
        }
        const open = /^ {0,3}(`{3,}|~{3,})\s*([^\s`]*)[^`]*$/.exec(line);
        if (open) {
            fence = { char: open[1][0], length: open[1].length, isChart: open[2] === 'chart' };
            body = [];
        }
    }
    return blocks;
}
/** Il primo problema in un report, o `null` se ogni grafico è valido. */
export function findChartError(markdown) {
    const blocks = extractChartBlocks(markdown);
    if (blocks.length > CHART_LIMITS.maxChartsPerReport) {
        return {
            code: 'too_many_charts',
            message: `A report can hold at most ${CHART_LIMITS.maxChartsPerReport} charts`,
        };
    }
    for (let i = 0; i < blocks.length; i++) {
        const result = parseChartSpec(blocks[i]);
        if (!result.ok) {
            return { code: result.error.code, message: `Chart ${i + 1}: ${result.error.message}` };
        }
    }
    return null;
}
